import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logoutUser from "../../utils/logout";
import { getSidebarManagements, getManagementForPath } from "../master/scaiManagementConfig";

// All managements are sourced from scaiManagementConfig.js. No permission filtering.
const BREAKPOINT = 1200;
const EXPANDED_KEY = "scaiSidebarExpanded";
const MOBILE_EVENT = "scai:toggle-mobile-sidebar";

export default function Header() {
  const { pathname } = useLocation();
  // Always start with icon-only navigation on desktop, regardless of old template storage.
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < BREAKPOINT);
  const managements = getSidebarManagements();
  const active = getManagementForPath(pathname);

  useEffect(() => {
    const onResize = () => {
      const isMobile = window.innerWidth < BREAKPOINT;
      setMobile(isMobile);
      if (!isMobile) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("scai-sidebar-ready");
    root.classList.toggle("scai-sidebar-wide", expanded && !mobile);
    root.classList.toggle("layout-menu-collapsed", !expanded || mobile);
    root.classList.toggle("scai-mobile-sidebar-open", mobile && mobileOpen);
    // Keep legacy template mobile class in sync with the actual drawer.
    root.classList.toggle("layout-menu-expanded", mobile && mobileOpen);
    root.style.setProperty("--scai-nav-width", !mobile && expanded ? "248px" : "76px");
    try {
      localStorage.setItem(EXPANDED_KEY, String(expanded));
      localStorage.setItem("myToggle", String(!expanded));
      localStorage.setItem("myToggleSide", String(!(mobile && mobileOpen)));
    } catch (_) { }
    return () => {
      root.classList.remove("scai-sidebar-ready", "scai-sidebar-wide", "scai-mobile-sidebar-open", "layout-menu-expanded");
      root.style.removeProperty("--scai-nav-width");
    };
  }, [expanded, mobile, mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const toggleMobile = () => setMobileOpen(prev => !prev);
    const closeOnEscape = event => { if (event.key === "Escape") setMobileOpen(false); };
    window.addEventListener(MOBILE_EVENT, toggleMobile);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener(MOBILE_EVENT, toggleMobile);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const onToggle = () => mobile ? setMobileOpen(false) : setExpanded(prev => !prev);

  return (
    <>
      {mobile && mobileOpen && (
        <button type="button" className="scai-side-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />
      )}
      <aside id="layout-menu" className="layout-menu menu-vertical menu bg-menu-theme scai-responsive-sidebar" aria-label="School ERP sidebar">
        <div className="scai-side-brand">
          <Link to={managements[0]?.path || "/"} className="scai-side-logo" title="SCAI Home" onClick={() => setMobileOpen(false)}>SCAI</Link>
          <div className="scai-side-brand-text"><strong>School ERP</strong><small>Management</small></div>
          <button type="button" className="scai-side-toggle" onClick={onToggle}
            title={mobile ? "Close sidebar" : expanded ? "Collapse sidebar" : "Expand sidebar"}
            aria-label={mobile ? "Close sidebar" : expanded ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={mobile ? mobileOpen : expanded}>
            <i
              className={
                mobile
                  ? "ri-close-line"
                  : expanded
                    ? "ri-arrow-left-s-line"
                    : "ri-arrow-right-s-line"
              }
            />
          </button>
        </div>
        <nav className="scai-side-scroll" aria-label="Management modules">
          {managements.map(item => {
            const name = item.label || item.title || item.key;
            const selected = active?.key === item.key;
            return (
              <Link key={item.key} to={item.path || item.defaultPath || "/"}
                className={`scai-side-link${selected ? " active" : ""}`}
                title={!expanded && !mobile ? name : undefined}
                aria-label={name} aria-current={selected ? "page" : undefined}
                onClick={() => setMobileOpen(false)}>
                <span className="scai-side-icon"><i className={item.icon || "ri-apps-2-line"} aria-hidden="true" /></span>
                <span className="scai-side-name">{name}</span>
              </Link>
            );
          })}
        </nav>
        <div className="scai-side-footer">
          <button type="button" className="scai-side-link scai-side-logout" onClick={logoutUser} title="Logout" aria-label="Logout">
            <span className="scai-side-icon"><i className="ri-logout-box-r-line" aria-hidden="true" /></span>
            <span className="scai-side-name">Logout</span>
          </button>
        </div>
      </aside>
      <style>{`
        html.scai-sidebar-ready .scai-responsive-sidebar {
          --side-bg:var(--bs-card-bg,#fff); --side-fg:var(--bs-body-color,#344054);
          --side-muted:var(--bs-secondary-color,#838b9f); --side-line:var(--bs-border-color,#e7eaf1);
          position:fixed!important;top:0!important;bottom:0!important;left:0!important;
          width:var(--scai-nav-width,76px)!important;min-width:var(--scai-nav-width,76px)!important;
          max-width:var(--scai-nav-width,76px)!important;height:100dvh!important;
          display:flex!important;flex-direction:column!important;overflow:hidden!important;
          background:var(--side-bg)!important;border-right:1px solid var(--side-line)!important;
          box-shadow:none!important;z-index:1080!important;
          transition:width .2s ease,min-width .2s ease,max-width .2s ease;
        }
        html.scai-sidebar-ready .scai-side-brand{height:76px;min-height:76px;position:relative;flex:0 0 76px;display:flex;align-items:flex-start;justify-content:center;padding:11px 6px;border-bottom:1px solid var(--side-line);gap:11px;}
        .scai-side-logo{width:39px;height:39px;display:grid;place-items:center;flex-shrink:0;background:var(--side-bg);border:1px solid var(--side-line);border-radius:11px;color:var(--side-fg)!important;font:700 12px Georgia,serif;text-decoration:none!important;}
        .scai-side-brand-text{display:none;flex-direction:column;justify-content:center;flex:1;min-width:0;white-space:nowrap;line-height:1.3;color:var(--side-fg)}
        .scai-side-brand-text strong{font-size:13px}.scai-side-brand-text small{font-size:10px;color:var(--side-muted)}
        .scai-side-toggle{position:absolute;right:3px;bottom:3px;width:22px;height:22px;padding:0;display:grid;place-items:center;border:1px solid var(--side-line);border-radius:6px;background:var(--side-bg);color:var(--side-muted);cursor:pointer;}
        .scai-side-toggle:hover{background:rgba(105,108,255,.10);color:#696cff}.scai-side-toggle i{font-size:13px}
        .scai-side-scroll{flex:1;min-height:0;overflow-y:auto!important;overflow-x:hidden!important;overscroll-behavior:contain;padding:10px 5px!important;scrollbar-width:thin;}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-link{display:flex!important;flex-direction:column!important;align-items:center!important;justify-content:center!important;gap:5px!important;width:100%!important;min-height:65px!important;padding:7px 2px!important;margin:0 0 5px!important;border:1px solid transparent;border-radius:11px;background:transparent;color:var(--side-muted)!important;text-decoration:none!important;cursor:pointer;text-align:center;white-space:normal!important;}
        .scai-side-icon{display:grid;place-items:center;flex:0 0 24px;width:25px;height:25px;font-size:21px;line-height:1;color:inherit}
        .scai-side-icon i{font-size:inherit!important;line-height:1!important}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-name{display:block!important;max-width:100%;font-size:9px!important;font-weight:700;line-height:1.18;word-break:break-word;overflow-wrap:anywhere;}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-link:hover{background:rgba(105,108,255,.08);color:#696cff!important}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-link.active{background:linear-gradient(135deg,#7467ff,#6554ef);color:white!important;box-shadow:0 3px 12px rgba(105,108,255,.18)}
        .scai-side-footer{flex:0 0 auto;padding:7px 5px;border-top:1px solid var(--side-line)}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-footer .scai-side-link{min-height:56px!important;margin:0!important}
        html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-logout:hover{background:rgba(234,84,85,.08)!important;color:#ea5455!important}
        html.scai-sidebar-wide .scai-responsive-sidebar .scai-side-brand{justify-content:flex-start;align-items:center;padding:11px 12px;}
        html.scai-sidebar-wide .scai-side-brand-text{display:flex}
        html.scai-sidebar-wide .scai-side-toggle{position:static;flex:0 0 29px;width:29px;height:29px}
        html.scai-sidebar-wide .scai-side-scroll{padding:10px 8px!important}
        html.scai-sidebar-wide .scai-responsive-sidebar .scai-side-link{min-height:47px!important;flex-direction:row!important;justify-content:flex-start!important;text-align:left!important;gap:12px!important;padding:7px 11px!important;border-radius:10px}
        html.scai-sidebar-wide .scai-responsive-sidebar .scai-side-name{font-size:12px!important;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        html.scai-sidebar-wide .scai-side-icon{width:27px;flex-basis:27px;font-size:21px}
        [data-style="dark"] .scai-responsive-sidebar,[data-bs-theme="dark"] .scai-responsive-sidebar{--side-bg:#2b2c40!important;--side-fg:#e9eaf4!important;--side-muted:#b1b4ce!important;--side-line:#484963!important}
        @media(min-width:1200px){
          html.scai-sidebar-ready .layout-page{padding-left:var(--scai-nav-width,76px)!important;margin-left:0!important;min-width:0;}
          html.scai-sidebar-ready .layout-menu-toggle{display:none!important}
        }
        .scai-side-backdrop{display:none}
        @media(max-width:1199.98px){
          html.scai-sidebar-ready .scai-responsive-sidebar{width:260px!important;min-width:260px!important;max-width:260px!important;transform:translateX(-102%)!important;transition:transform .22s ease!important;}
          html.scai-mobile-sidebar-open .scai-responsive-sidebar{transform:translateX(0)!important}
          html.scai-sidebar-ready .layout-page{padding-left:0!important;margin-left:0!important;}
          html.scai-sidebar-ready .scai-side-brand{justify-content:flex-start;align-items:center;padding:11px 12px}
          .scai-side-brand-text{display:flex}.scai-side-toggle{position:static;flex:0 0 29px;width:29px;height:29px}
          html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-link{min-height:48px!important;flex-direction:row!important;justify-content:flex-start!important;text-align:left!important;gap:13px!important;padding:8px 11px!important}
          html.scai-sidebar-ready .scai-responsive-sidebar .scai-side-name{font-size:12px!important;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
          .scai-side-scroll{padding:10px 9px!important}
          .scai-side-backdrop{position:fixed;inset:0;display:block;width:100vw;height:100dvh;border:0;padding:0;background:rgba(18,24,40,.53);z-index:1079}
        }
        @media print{html.scai-sidebar-ready .scai-responsive-sidebar,.scai-side-backdrop{display:none!important}html.scai-sidebar-ready .layout-page{padding-left:0!important}}
      `}</style>
    </>
  );
}
